import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ResendInviteButton } from '../../components/ResendInviteButton';
import { formatApiErrorDetail } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CSAInvites = () => {
  const [invites, setInvites] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadInvites = useCallback(async () => {
    setError('');
    try {
      const { data } = await axios.get(`${API}/csa/invites`, { withCredentials: true });
      setInvites(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadInvites(); }, [loadInvites]);
  const filtered = useMemo(() => invites.filter((invite) =>
    `${invite.client_name || ''} ${invite.client_email} ${invite.advisor_name}`.toLowerCase().includes(query.trim().toLowerCase())
  ), [invites, query]);

  return (
    <section className="space-y-4" data-testid="csa-client-invites">
      <div>
        <h2 className="text-lg font-semibold text-[#0f172a]" data-testid="csa-client-invites-heading">Client Profile Invitations</h2>
        <p className="text-sm text-[#475569]">Resend pending or expired invitations across the firm. Submitted profiles remain read-only.</p>
      </div>
      <Input value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-md"
        placeholder="Search client name, email, or advisor" aria-label="Search client invitations" data-testid="csa-invite-search" />
      {loading && <p role="status" data-testid="csa-invites-loading">Loading invitations…</p>}
      {error && <div role="alert" data-testid="csa-invites-error"><p className="text-sm text-red-600">{error}</p><Button variant="outline" onClick={loadInvites} data-testid="csa-invites-retry">Try Again</Button></div>}
      {!loading && !error && (
        <div className="bg-white border border-[#e2e8f0] rounded-lg divide-y divide-[#e2e8f0]" data-testid="csa-invite-list">
          {!filtered.length && <p className="p-6 text-sm text-[#475569]" data-testid="csa-invites-empty">{query ? 'No matching invitations.' : 'No client invitations yet.'}</p>}
          {filtered.map((invite) => (
            <article key={invite.id} className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`csa-invite-${invite.id}`}>
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold text-[#0f172a] break-words" data-testid={`csa-invite-name-${invite.id}`}>{invite.client_name || invite.client_email}</p>
                <p className="text-sm text-[#475569] break-all" data-testid={`csa-invite-email-${invite.id}`}>{invite.client_email}</p>
                <p className="text-xs text-[#475569] break-words" data-testid={`csa-invite-advisor-${invite.id}`}>Advisor: {invite.advisor_name}</p>
                <p className="text-xs text-[#475569]" data-testid={`csa-invite-expiry-${invite.id}`}>Expires {new Date(invite.expires_at).toLocaleDateString()}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <span className="rounded-full bg-[#f8fafc] border border-[#e2e8f0] px-2 py-1 text-xs text-[#475569]" data-testid={`csa-invite-status-${invite.id}`}>
                  {(['pending', 'opened'].includes(invite.status) && new Date(invite.expires_at) < new Date() ? 'expired' : invite.status).toUpperCase()}
                </span>
                {['pending', 'opened', 'expired'].includes(invite.status) && <ResendInviteButton
                  endpoint={`/csa/invites/${invite.id}/resend`} recipient={invite.client_email}
                  onResent={loadInvites} testId={`csa-resend-invite-${invite.id}`} />}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
